import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  TextInput,
  Select,
  Alert,
  Spinner,
  Badge,
  Pagination,
  Datepicker,
} from 'flowbite-react';
import Select2 from 'react-select';
import { fetchWithAuth } from '../utils/api';
import { getLeadsEndpoints } from '../config/api';
import {
  getValidationMessages,
  formatDate,
  formatDateTime,
} from '../utils/helper';
import { loadCampaignsList } from '../config/lib';
import Swal from 'sweetalert2';

// Status constants
export const LEAD_STATUS = {
  NEW: 'new',
  REVENUE_RECEIVED: 'revenue_received',
} as const;

export type LeadStatus =
  | typeof LEAD_STATUS.NEW
  | typeof LEAD_STATUS.REVENUE_RECEIVED;

// Lead type constants
export const LEAD_TYPES = {
  TEXT: 'text',
  COMMENT: 'comment',
  REFERRAL: 'referral',
  REFERRAL_ADS: 'referral_ads',
} as const;

export type LeadType =
  | typeof LEAD_TYPES.TEXT
  | typeof LEAD_TYPES.COMMENT
  | typeof LEAD_TYPES.REFERRAL
  | typeof LEAD_TYPES.REFERRAL_ADS;

export const FOLLOW_UP_STATUSES = [
  'Chưa push',
  'Sent push 2',
  'Sent push 3',
  'Sent push 4',
  'Sent push 5',
  'Sent Push 6',
  'Sent push 7',
  'Sent push 8',
  'Ko push',
] as const;

export const OFFLINE_STATUSES = [
  'Lead mới',
  'Chưa tiếp cận được',
  'Đang tư vấn',
  'Đang xem thêm',
  'Tiềm năng',
  'Chờ chuyển khoản',
  'Cọc',
  'Mua hàng',
  'Chăm sóc sau',
  'Sai số/Sai đối tượng',
  'Từ chối',
  'Trùng',
] as const;

export const RENEW_STATUSES = [
  'Chưa phản hồi',
  'Từ chối',
  'Đang xem thêm',
  'Hẹn Chuyển Khoản',
  'Đã Renew',
  'Đang chăm sóc',
  'Full phí',
  'Cọc',
  'Hoàn/Huỷ',
  'Không chất lượng',
  'Chờ xếp lớp',
  'Hẹn CK - Có ngày cụ thể',
] as const;

export const CRM_STATUSES = [
  'UQL',
  'KNM',
  'Khai thác',
  'Chuyển off',
  'Tư vấn',
  'ĐXT',
  'CCK',
  'CKK-Có ngày hẹn',
  'Cọc',
  'Cọc-Chưa học/Có hẹn',
  'Mua hàng',
  'Full phí',
  'Từ chối mua',
  'Bảo lưu',
  'KH hẹn ngày cụ thể',
  'Hoàn/ Hủy',
  'test',
  'Chờ xếp lớp',
  'k có sp phù hợp',
] as const;

interface Lead {
  id: number;
  customer_id: string;
  page_id: string;
  customer_name: string;
  comment_id: string;
  status: LeadStatus;
  crm_status: string;
  status_renew?: string;
  status_offline?: string;
  status_follow_up?: string;
  campaign: number;
  campaign_name?: string;
  lead_type: LeadType;
  type: string;
  is_new_customer: boolean;
  phone_numbers: string[];
  global_ids: string[];
  created_at: string;
}

interface Campaign {
  id: number;
  name: string;
}

interface LeadFormData {
  customer_id: string;
  customer_name?: string | null;
  status: LeadStatus;
  campaign: number;
  lead_type: LeadType;
  created_at: string;
}

interface StatusMultiSelectProps {
  options: readonly string[];
  value: string[];
  onChange: (value: string[]) => void;
  placeholder: string;
}

const StatusMultiSelect: React.FC<StatusMultiSelectProps> = ({
  options,
  value,
  onChange,
  placeholder,
}) => (
  <Select2
    isMulti
    closeMenuOnSelect={false}
    styles={{
      input: base => ({
        ...base,
        boxShadow: 'none',
        outline: 'none',
        border: 'none',
      }),
      control: baseStyles => ({
        ...baseStyles,
        minHeight: '42px',
      }),
    }}
    options={options.map(status => ({ value: status, label: status }))}
    value={value.map(status => ({ value: status, label: status }))}
    onChange={selected => onChange(selected.map(option => option.value))}
    placeholder={placeholder}
  />
);

const Leads: React.FC = () => {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);
  const [messagePopup, setMessagePopup] = useState<{
    type: 'success' | 'failure';
    text: string;
  } | null>(null);

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [hasCrmStatusFilter, setHasCrmStatusFilter] = useState('');
  const [crmStatusFilter, setCrmStatusFilter] = useState<string[]>([]);
  const [statusRenewFilter, setStatusRenewFilter] = useState<string[]>([]);
  const [statusOfflineFilter, setStatusOfflineFilter] = useState<string[]>([]);
  const [statusFollowUpFilter, setStatusFollowUpFilter] = useState<string[]>(
    []
  );
  const [isNewCustomerFilter, setIsNewCustomerFilter] = useState('');
  const [hasPhoneNumberFilter, setHasPhoneNumberFilter] = useState('');
  const [campaignFilter, setCampaignFilter] = useState<any>();
  const [createdAtFromFilter, setCreatedAtFromFilter] = useState<Date | null>(
    null
  );
  const [createdAtToFilter, setCreatedAtToFilter] = useState<Date | null>(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingLead, setEditingLead] = useState<Lead | null>(null);

  // Form states
  const [formData, setFormData] = useState<LeadFormData>({
    customer_id: '',
    status: LEAD_STATUS.NEW,
    campaign: 0,
    lead_type: LEAD_TYPES.TEXT,
    created_at: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Load leads
  const loadLeads = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const leadsEndpoint = getLeadsEndpoints().LIST;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
        ...(hasCrmStatusFilter && { has_crm_status: hasCrmStatusFilter }),
        ...(crmStatusFilter.length && {
          crm_status: crmStatusFilter.join(','),
        }),
        ...(statusRenewFilter.length && {
          status_renew: statusRenewFilter.join(','),
        }),
        ...(statusOfflineFilter.length && {
          status_offline: statusOfflineFilter.join(','),
        }),
        ...(statusFollowUpFilter.length && {
          status_follow_up: statusFollowUpFilter.join(','),
        }),
        ...(campaignFilter && { campaign: campaignFilter.value }),
        ...(createdAtFromFilter && {
          date_created_from: formatDate(createdAtFromFilter),
        }),
        ...(createdAtToFilter && {
          date_created_to: formatDate(createdAtToFilter),
        }),
        ...(isNewCustomerFilter && { is_new_customer: isNewCustomerFilter }),
        ...(hasPhoneNumberFilter && {
          has_phone_number: hasPhoneNumberFilter,
        }),
      });

      const response = await fetchWithAuth(`${leadsEndpoint}?${params}`);

      if (response.success && response.data) {
        setLeads(response.data.results || []);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        setTotalItems(response.data.count || 0);
      } else {
        setError('Không thể tải danh sách leads');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi khi tải dữ liệu');
      console.error('Load leads error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setHasCrmStatusFilter('');
    setCrmStatusFilter([]);
    setStatusRenewFilter([]);
    setStatusOfflineFilter([]);
    setStatusFollowUpFilter([]);
    setCampaignFilter(null);
    setCreatedAtFromFilter(null);
    setCreatedAtToFilter(null);
    setIsNewCustomerFilter('');
    setHasPhoneNumberFilter('');
  };

  // Load campaigns
  const loadCampaigns = async () => {
    const campaignsList = await loadCampaignsList();
    setCampaigns(campaignsList);
  };

  useEffect(() => {
    loadLeads();
    loadCampaigns();
  }, []);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    loadLeads(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadLeads(page);
  };

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setEditingLead(null);
    setFormData({
      customer_id: '',
      status: LEAD_STATUS.NEW,
      campaign: 0,
      lead_type: LEAD_TYPES.TEXT,
      created_at: formatDate(new Date()),
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Open edit modal
  const openEditModal = (lead: Lead) => {
    setModalMode('edit');
    setEditingLead(lead);
    setFormData({
      customer_id: lead.customer_id,
      status: lead.status,
      campaign: lead.campaign,
      lead_type: lead.lead_type,
      created_at: lead.created_at,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Handle form input change
  const handleInputChange = (field: keyof LeadFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.customer_id.trim()) {
      errors.customer_id = 'Customer ID là bắt buộc';
    } else if (formData.customer_id.length > 100) {
      errors.customer_id = 'Customer ID không được vượt quá 100 ký tự';
    }

    if (!formData.campaign || formData.campaign === 0) {
      errors.campaign = 'Campaign là bắt buộc';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const leadsEndpoint = getLeadsEndpoints();
      const endpoint =
        modalMode === 'create'
          ? leadsEndpoint.CREATE
          : `${leadsEndpoint.UPDATE}/${editingLead?.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetchWithAuth(endpoint, {
        method,
        body: JSON.stringify({
          customer_id: formData.customer_id.trim(),
          status: formData.status,
          campaign: formData.campaign,
          lead_type: formData.lead_type,
          created_at: formData.created_at,
        }),
      });

      if (response.success) {
        setMessagePopup({
          type: 'success',
          text: `Lead ${
            modalMode === 'create' ? 'đã được tạo' : 'đã được cập nhật'
          } thành công!`,
        });
        setShowModal(false);
        loadLeads(currentPage);
      } else {
        setMessagePopup({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            `Không thể ${modalMode === 'create' ? 'tạo' : 'cập nhật'} lead`,
        });
      }
    } catch (error) {
      setMessagePopup({
        type: 'failure',
        text: `Đã xảy ra lỗi khi ${
          modalMode === 'create' ? 'tạo' : 'cập nhật'
        } lead`,
      });
      console.error('Submit lead error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  // Handle delete
  const handleDelete = async (leadId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa lead này?')) {
      return;
    }

    try {
      const leadsEndpoint = getLeadsEndpoints();
      const response = await fetchWithAuth(
        `${leadsEndpoint.DELETE}/${leadId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.success) {
        setMessage({
          type: 'success',
          text: 'Lead đã được xóa thành công!',
        });
        loadLeads(currentPage);
      } else {
        setMessage({
          type: 'failure',
          text: 'Không thể xóa lead',
        });
      }
    } catch (error) {
      setMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi xóa lead',
      });
      console.error('Delete lead error:', error);
    }
  };

  // Get status badge color
  const getStatusBadgeColor = (status: LeadStatus) => {
    return status === LEAD_STATUS.NEW ? 'blue' : 'green';
  };

  // Get status display text
  const getStatusText = (status: LeadStatus) => {
    return status === LEAD_STATUS.NEW ? 'Mới' : 'Đã nhận doanh thu';
  };

  // Get lead type display text
  const getLeadTypeText = (type: LeadType) => {
    const typeMap: Record<LeadType, string> = {
      [LEAD_TYPES.TEXT]: 'Text',
      [LEAD_TYPES.COMMENT]: 'Comment',
      [LEAD_TYPES.REFERRAL]: 'Referral',
      [LEAD_TYPES.REFERRAL_ADS]: 'Referral Ads',
    };
    return typeMap[type] || type;
  };

  const handlePancakeConversation = (lead: Lead) => {
    if (lead.type == 'facebook') {
      window.open(
        `https://pancake.vn/${lead.page_id}?c_id=${
          lead.page_id + '_' + lead.customer_id
        }`,
        '_blank'
      );
      return;
    }
    if (lead.type == 'tiktok') {
      window.open(
        `https://pancake.vn/${lead.page_id}?c_id=${lead.comment_id}`,
        '_blank'
      );
      return;
    }
    Swal.fire({
      icon: 'warning',
      title: 'Lỗi',
      text: 'Chỉ hỗ trợ hội thoại trên Facebook và TikTok',
    });
  };

  if (loading && leads.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">Đang tải danh sách leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Leads</h1>
            <p className="text-gray-600">Quản lý các leads</p>
          </div>
          <Button onClick={openCreateModal} className="btn-primary">
            <svg
              className="w-4 h-4 mr-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 4v16m8-8H4"
              />
            </svg>
            Tạo Lead
          </Button>
        </div>
      </div>

      {message && (
        <Alert color={message.type} className="mb-4">
          <span>{message.text}</span>
        </Alert>
      )}

      {/* Search and Filter */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <TextInput
              placeholder="Tìm kiếm theo Customer ID..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <StatusMultiSelect
              options={CRM_STATUSES}
              value={crmStatusFilter}
              onChange={setCrmStatusFilter}
              placeholder="Trạng thái CRM..."
            />
            <StatusMultiSelect
              options={RENEW_STATUSES}
              value={statusRenewFilter}
              onChange={setStatusRenewFilter}
              placeholder="Trạng thái Renew..."
            />
            <StatusMultiSelect
              options={OFFLINE_STATUSES}
              value={statusOfflineFilter}
              onChange={setStatusOfflineFilter}
              placeholder="Trạng thái Offline..."
            />
            <StatusMultiSelect
              options={FOLLOW_UP_STATUSES}
              value={statusFollowUpFilter}
              onChange={setStatusFollowUpFilter}
              placeholder="Trạng thái Follow Up..."
            />

            <Select
              value={hasCrmStatusFilter}
              onChange={e => setHasCrmStatusFilter(e.target.value)}
            >
              <option value="">Trạng thái (Có/Không)</option>
              <option value="True">Có</option>
              <option value="False">Không</option>
            </Select>

            <Select
              value={isNewCustomerFilter}
              onChange={e => setIsNewCustomerFilter(e.target.value)}
            >
              <option value="">Khách mới? (Có/Không)</option>
              <option value="True">Có</option>
              <option value="False">Không</option>
            </Select>
            <Select
              value={hasPhoneNumberFilter}
              onChange={e => setHasPhoneNumberFilter(e.target.value)}
            >
              <option value="">Số điện thoại (Có/Không)</option>
              <option value="True">Có</option>
              <option value="False">Không</option>
            </Select>
            <Select2
              styles={{
                input: base => ({
                  ...base,
                  boxShadow: 'none',
                  outline: 'none',
                  border: 'none',
                }),
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  height: '42px',
                }),
              }}
              options={campaigns.map(campaign => ({
                value: campaign.id,
                label: campaign.name,
              }))}
              onChange={ob => setCampaignFilter(ob)}
              value={
                campaignFilter
                  ? {
                      value: campaignFilter?.value,
                      label: campaignFilter?.label,
                    }
                  : null
              }
              isClearable
              placeholder="Chọn Campaign..."
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
            <Datepicker
              icon={undefined}
              value={createdAtFromFilter ? formatDate(createdAtFromFilter) : ''}
              title="Từ ngày tạo"
              placeholder="Từ ngày tạo"
              onSelectedDateChanged={e => {
                setCreatedAtFromFilter(e);
              }}
              showClearButton={false}
              maxDate={createdAtToFilter || new Date()}
              theme={{
                views: {
                  days: {
                    items: {
                      base: 'grid w-64 grid-cols-7',
                      item: {
                        base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                        selected:
                          'bg-primary-700 text-blue-500 hover:bg-primary-600',
                        disabled: 'text-gray-500 line-through',
                      },
                    },
                  },
                },
              }}
            />
            <Datepicker
              icon={undefined}
              value={createdAtToFilter ? formatDate(createdAtToFilter) : ''}
              title="Đến ngày tạo"
              placeholder="Đến ngày tạo"
              onSelectedDateChanged={e => {
                setCreatedAtToFilter(e);
              }}
              showClearButton={false}
              minDate={createdAtFromFilter || undefined}
              maxDate={new Date()}
              theme={{
                views: {
                  days: {
                    items: {
                      base: 'grid w-64 grid-cols-7',
                      item: {
                        base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                        selected:
                          'bg-primary-700 text-blue-500 hover:bg-primary-600',
                        disabled: 'text-gray-500 line-through',
                      },
                    },
                  },
                },
              }}
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mt-4">
            <Button
              onClick={handleSearch}
              type="button"
              className="btn-primary"
            >
              Tìm kiếm
            </Button>
            <Button
              onClick={handleClearFilters}
              type="button"
              className="btn-reset"
            >
              Xóa bộ lọc
            </Button>
          </div>
        </div>
      </Card>

      {/* Leads Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.HeadCell>Customer ID</Table.HeadCell>
              <Table.HeadCell>Tên Khách Hàng</Table.HeadCell>
              <Table.HeadCell>Trạng thái</Table.HeadCell>
              <Table.HeadCell>Trạng thái CRM</Table.HeadCell>
              <Table.HeadCell>Trạng thái Renew</Table.HeadCell>
              <Table.HeadCell>Trạng thái Offline</Table.HeadCell>
              <Table.HeadCell>Trạng thái Follow Up</Table.HeadCell>
              <Table.HeadCell>Campaign</Table.HeadCell>
              <Table.HeadCell>Loại Lead</Table.HeadCell>
              <Table.HeadCell>Nguồn</Table.HeadCell>
              <Table.HeadCell>Khách mới</Table.HeadCell>
              <Table.HeadCell>Số điện thoại</Table.HeadCell>
              <Table.HeadCell>Ngày tạo</Table.HeadCell>
              <Table.HeadCell>Hành động</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {leads.map(lead => (
                <Table.Row
                  key={lead.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {lead.customer_id}
                  </Table.Cell>
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {lead.customer_name}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge color={getStatusBadgeColor(lead.status)}>
                      {getStatusText(lead.status)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="yellow">{lead.crm_status ?? '-'}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="indigo">{lead.status_renew ?? '-'}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="pink">{lead.status_offline ?? '-'}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="purple">{lead.status_follow_up ?? '-'}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="blue">
                      {lead.campaign_name || `Campaign ${lead.campaign}`}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="purple">
                      {getLeadTypeText(lead.lead_type)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="purple">{lead.type}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {lead.is_new_customer ? (
                      <Badge color="green">Có</Badge>
                    ) : (
                      <Badge color="red">Không</Badge>
                    )}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {lead.phone_numbers.join(', ')}
                  </Table.Cell>

                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDateTime(lead.created_at)}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        color="green"
                        onClick={() => handlePancakeConversation(lead)}
                      >
                        Hội thoại Pancake
                      </Button>
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => openEditModal(lead)}
                      >
                        Sửa
                      </Button>
                      <Button
                        size="sm"
                        color="red"
                        onClick={() => handleDelete(lead.id.toString())}
                      >
                        Xóa
                      </Button>
                    </div>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between p-4">
          <div className="text-sm text-gray-700">
            Hiển thị {(currentPage - 1) * itemsPerPage + 1} đến{' '}
            {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng số{' '}
            {totalItems} kết quả
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={handlePageChange}
          />
        </div>
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="4xl">
        <Modal.Header>
          {modalMode === 'create' ? 'Tạo Lead Mới' : 'Chỉnh Sửa Lead'}
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {messagePopup && (
              <Alert color={messagePopup.type} className="mb-4">
                <span>{messagePopup.text}</span>
              </Alert>
            )}
            <div className="space-y-4">
              {/* Customer ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Customer ID *
                </label>
                <TextInput
                  value={formData.customer_id}
                  onChange={e =>
                    handleInputChange('customer_id', e.target.value)
                  }
                  placeholder="Nhập Customer ID (tối đa 100 ký tự)"
                  maxLength={100}
                  color={formErrors.customer_id ? 'failure' : undefined}
                  helperText={formErrors.customer_id}
                />
              </div>

              {/* Campaign */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Campaign
                  {modalMode === 'edit' ? ' (Không thể chỉnh sửa)' : '*'}
                </label>

                <Select2
                  styles={{
                    input: base => ({
                      ...base,
                      boxShadow: 'none',
                      outline: 'none',
                      border: 'none',
                    }),
                    control: (baseStyles, state) => ({
                      ...baseStyles,
                      height: '42px',
                    }),
                  }}
                  options={campaigns.map(campaign => ({
                    value: campaign.id,
                    label: campaign.name,
                  }))}
                  onChange={e =>
                    handleInputChange(
                      'campaign',
                      parseInt(e?.value?.toString() || '0')
                    )
                  }
                  value={
                    formData.campaign
                      ? {
                          value: formData.campaign,
                          label:
                            campaigns.find(
                              campaign => campaign.id === formData.campaign
                            )?.name || '',
                        }
                      : null
                  }
                  isClearable
                  placeholder="Chọn Campaign..."
                  isDisabled={modalMode === 'edit'}
                />
                {formErrors.campaign && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.campaign}
                  </p>
                )}
              </div>

              {/* Status and Lead Type */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Status *
                  </label>
                  <Select
                    value={formData.status}
                    onChange={e =>
                      handleInputChange('status', e.target.value as LeadStatus)
                    }
                  >
                    <option value={LEAD_STATUS.NEW}>
                      {getStatusText(LEAD_STATUS.NEW)}
                    </option>
                    <option value={LEAD_STATUS.REVENUE_RECEIVED}>
                      {getStatusText(LEAD_STATUS.REVENUE_RECEIVED)}
                    </option>
                  </Select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Loại Lead *
                  </label>
                  <Select
                    value={formData.lead_type}
                    onChange={e =>
                      handleInputChange('lead_type', e.target.value as LeadType)
                    }
                  >
                    <option value={LEAD_TYPES.TEXT}>
                      {getLeadTypeText(LEAD_TYPES.TEXT)}
                    </option>
                    <option value={LEAD_TYPES.COMMENT}>
                      {getLeadTypeText(LEAD_TYPES.COMMENT)}
                    </option>
                    <option value={LEAD_TYPES.REFERRAL}>
                      {getLeadTypeText(LEAD_TYPES.REFERRAL)}
                    </option>
                    <option value={LEAD_TYPES.REFERRAL_ADS}>
                      {getLeadTypeText(LEAD_TYPES.REFERRAL_ADS)}
                    </option>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ngày tính LEAD
                  </label>
                  <div>
                    <Datepicker
                      value={formatDate(formData.created_at)}
                      defaultDate={new Date(formData.created_at)}
                      icon={undefined}
                      onSelectedDateChanged={e => {
                        handleInputChange('created_at', formatDate(e));
                      }}
                      color={formErrors.created_at ? 'failure' : undefined}
                      helperText={formErrors.created_at}
                      maxDate={new Date()}
                      theme={{
                        popup: {
                          root: {
                            base: 'fixed z-[9999] block pt-2',
                          },
                        },
                        views: {
                          days: {
                            items: {
                              base: 'grid w-64 grid-cols-7',
                              item: {
                                base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                                selected:
                                  'bg-primary-700 text-blue-500 hover:bg-primary-600',
                                disabled: 'text-gray-500 line-through',
                              },
                            },
                          },
                        },
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="gray"
              onClick={() => setShowModal(false)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Đang xử lý...
                </>
              ) : modalMode === 'create' ? (
                'Tạo Lead'
              ) : (
                'Cập nhật'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default Leads;
